import { ComponentType, ReactNode } from "react";

import { IconName } from "../common/Icon";
import { SelectionCanceller } from "./SelectionCanceller";

export type DisplayType = "popover" | "dropdown" | "small";

export interface IInfoComponentProps {
    onBack: () => void;
    onSelect: () => void;
}

export interface IPickerGroup<T extends string = string> {
    id: T;
    icon?: IconName;
    label: string;
}

export interface IPickerTab<T extends string = string> {
    id: T;
    icon?: IconName;
    label: string;
}

export interface IPickerItemCommon<
    TTab extends string = string,
    TGroup extends string = string,
> {
    id?: string | number;
    tab?: TTab;
    group?: TGroup;
    disabled?: boolean;
}

export interface IPickerButton<
    TTab extends string = string,
    TGroup extends string = string,
> extends IPickerItemCommon<TTab, TGroup> {
    icon?: IconName | null;
    badge?: ReactNode;
    label: string;
    checked?: boolean;
    onClick?: (
        this: IPickerButton<TTab, TGroup>,
        cancel: SelectionCanceller,
    ) => void;
    InfoComponent?: ComponentType<IInfoComponentProps>;
}

export interface IPickerLink<
    TTab extends string = string,
    TGroup extends string = string,
> extends IPickerItemCommon<TTab, TGroup> {
    link: string;
    onClick?: (
        this: IPickerLink<TTab, TGroup>,
        cancel: SelectionCanceller,
    ) => void;
}

export type PickerItem<
    TTab extends string = string,
    TGroup extends string = string,
> = IPickerButton<TTab, TGroup> | IPickerLink<TTab, TGroup>;

export function isPickerLink<
    TTab extends string = string,
    TGroup extends string = string,
>(item: PickerItem<TTab, TGroup>): item is IPickerLink<TTab, TGroup> {
    return "link" in item;
}

export function isPickerButton<
    TTab extends string = string,
    TGroup extends string = string,
>(item: PickerItem<TTab, TGroup>): item is IPickerButton<TTab, TGroup> {
    return "label" in item;
}

export type PickerFilteredItems<T extends string, U extends string> =
    | Set<PickerItem<T, U>>
    | Map<IPickerTab<T | "never">, Set<PickerItem<T, U>>>;
